import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Printer, Trash2 } from 'lucide-react';
import { useApps, useUserQuery, useUpdateSettings } from '../lib/hooks';

interface AppInfo {
  keyword: string;
  aliases?: string[];
  helpText: string;
  usage: string;
}

interface AppsResponse {
  serviceNumber?: string;
  apps?: AppInfo[];
}

interface Contact {
  name: string;
  email: string;
}

interface AppConfig {
  msg?: { contacts?: Contact[] };
  news?: { topic?: string };
  fire?: { radiusMiles?: number };
  sun?: { tz?: string };
  [key: string]: unknown;
}

interface UserInfo {
  appConfig?: AppConfig;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const READ_ONLY_KEYWORDS = new Set(['WX', 'EMAIL', 'SNOW', 'TRAIL']);
const NEWS_TOPICS = ['general', 'tech', 'business'];
const SUN_TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
];
const DEFAULT_RADIUS = 50;
const DEFAULT_TZ = 'America/Los_Angeles';
const MIN_RADIUS = 10;
const MAX_RADIUS = 200;

const AppCard: React.FC<{ app: AppInfo }> = ({ app }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-base font-bold text-gray-900">{app.keyword}</p>
          <p className="mt-1 text-sm text-gray-600">{app.helpText}</p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          aria-label={expanded ? `hide ${app.keyword} usage` : `show ${app.keyword} usage`}
          className="ml-4 p-1 text-gray-400 hover:text-gray-600"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {expanded && <p className="mt-3 text-sm text-gray-500 border-t border-gray-100 pt-3">{app.usage}</p>}
    </div>
  );
};

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
    {children}
  </div>
);

const inputClass =
  'block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-gray-500 focus:ring-gray-500';

const ContactsEditor: React.FC<{
  contacts: Contact[];
  onChange: (contacts: Contact[]) => void;
}> = ({ contacts, onChange }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const handleAdd = () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || !EMAIL_RE.test(trimmedEmail)) {
      setError('Enter a name and a valid email address.');
      return;
    }
    setError('');
    onChange([...contacts, { name: trimmedName, email: trimmedEmail }]);
    setName('');
    setEmail('');
  };

  const handleRemove = (index: number) => {
    onChange(contacts.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="space-y-2 mb-4">
        {contacts.length === 0 && (
          <p className="text-sm text-gray-500">No contacts saved yet.</p>
        )}
        {contacts.map((contact, index) => (
          <div
            key={`${contact.email}-${index}`}
            className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2"
          >
            <div className="flex items-center gap-3 text-sm text-gray-900">
              <input
                aria-label={`contact ${index} name`}
                className={`${inputClass} w-32`}
                value={contact.name}
                readOnly
                disabled
              />
              <input
                aria-label={`contact ${index} email`}
                className={`${inputClass} w-56`}
                value={contact.email}
                readOnly
                disabled
              />
            </div>
            <button
              type="button"
              onClick={() => handleRemove(index)}
              aria-label={`remove ${contact.name}`}
              className="ml-3 p-1 text-gray-400 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="new-contact-name">
            Contact name
          </label>
          <input
            id="new-contact-name"
            className={`${inputClass} w-32`}
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="new-contact-email">
            Contact email
          </label>
          <input
            id="new-contact-email"
            type="email"
            className={`${inputClass} w-56`}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="inline-flex items-center px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add contact
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

const Apps: React.FC = () => {
  const { data: appsData } = useApps() as { data?: AppsResponse };
  const { data: user } = useUserQuery() as { data?: UserInfo };
  const { mutate } = useUpdateSettings();

  const apps = appsData?.apps || [];
  const appConfig = user?.appConfig || {};
  const contacts = appConfig.msg?.contacts || [];
  const [radiusInput, setRadiusInput] = useState(
    String(appConfig.fire?.radiusMiles ?? DEFAULT_RADIUS)
  );

  useEffect(() => {
    if (appConfig.fire?.radiusMiles !== undefined) {
      setRadiusInput(String(appConfig.fire.radiusMiles));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appConfig.fire?.radiusMiles]);

  const saveAppConfig = (patch: AppConfig) => {
    mutate({ app_config: patch });
  };

  const handleRadiusBlur = () => {
    const parsed = parseInt(radiusInput, 10);
    const clamped = Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, isNaN(parsed) ? DEFAULT_RADIUS : parsed));
    setRadiusInput(String(clamped));
    saveAppConfig({ fire: { radiusMiles: clamped } });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Apps</h2>
        {appsData?.serviceNumber && (
          <p className="mt-1 text-sm text-gray-500">
            Text any keyword below to {appsData.serviceNumber}.
          </p>
        )}
      </div>

      <div className="print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {apps.map(app => (
            <AppCard key={app.keyword} app={app} />
          ))}
        </div>

        <div className="space-y-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900">Configure</h3>

          <SectionCard title="MSG / CHECKIN contacts">
            <ContactsEditor
              contacts={contacts}
              onChange={newContacts => saveAppConfig({ msg: { contacts: newContacts } })}
            />
          </SectionCard>

          <SectionCard title="NEWS">
            <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="news-topic">
              Default topic
            </label>
            <select
              id="news-topic"
              className={`${inputClass} w-48`}
              value={appConfig.news?.topic || 'general'}
              onChange={e => saveAppConfig({ news: { topic: e.target.value } })}
            >
              {NEWS_TOPICS.map(topic => (
                <option key={topic} value={topic}>
                  {topic}
                </option>
              ))}
            </select>
          </SectionCard>

          <SectionCard title="FIRE">
            <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="fire-radius">
              Radius (miles)
            </label>
            <input
              id="fire-radius"
              type="number"
              min={MIN_RADIUS}
              max={MAX_RADIUS}
              className={`${inputClass} w-32`}
              value={radiusInput}
              onChange={e => setRadiusInput(e.target.value)}
              onBlur={handleRadiusBlur}
            />
          </SectionCard>

          <SectionCard title="SUN">
            <label className="block text-xs font-medium text-gray-500 mb-1" htmlFor="sun-tz">
              Time zone
            </label>
            <select
              id="sun-tz"
              className={`${inputClass} w-64`}
              value={appConfig.sun?.tz || DEFAULT_TZ}
              onChange={e => saveAppConfig({ sun: { tz: e.target.value } })}
            >
              {SUN_TIMEZONES.map(tz => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </SectionCard>

          {apps
            .filter(app => READ_ONLY_KEYWORDS.has(app.keyword))
            .map(app => (
              <SectionCard key={app.keyword} title={app.keyword}>
                <p className="text-sm text-gray-500">
                  No settings — text {app.keyword} ? for usage.
                </p>
              </SectionCard>
            ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Cheat sheet</h3>
          <button
            type="button"
            onClick={() => window.print()}
            className="print:hidden inline-flex items-center px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
          >
            <Printer className="w-4 h-4 mr-1" />
            Print
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <th className="py-2 pr-4">Keyword</th>
                <th className="py-2">Usage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {apps.map(app => (
                <tr key={app.keyword}>
                  <td className="py-2 pr-4 font-bold text-gray-900 align-top whitespace-nowrap">
                    {app.keyword}
                  </td>
                  <td className="py-2 text-gray-600">{app.usage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Apps;
